import { Box } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import ReactFlow, {
  Background,
  Controls,
  Node,
  ReactFlowProvider,
  Viewport,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow'
import { YARD_DRAWERS_WIDTH } from '../../helpers/yard-view.utils'
import { useBerthNodes } from '../../hooks/use-berth-nodes'
import { useFlexibleBlockNodes } from '../../hooks/use-flexible-block-nodes'
import { useGeoAnchor } from '../../hooks/use-geo-anchor'
import { useLayoutObjectNodes } from '../../hooks/use-layout-object-nodes'
import { useRailTrackNodes } from '../../hooks/use-rail-track-nodes'
import { useRoadNetworkNodes } from '../../hooks/use-road-network-nodes'
import { useRoadNodeDrag } from '../../hooks/use-road-node-drag'
import { useYardBlockNodes } from '../../hooks/use-yard-block-nodes'
import { useElasticViewport } from '../../hooks/use-elastic-viewport'
import { useYardRoadDrawing } from '../../hooks/use-yard-road-drawing'
import { BerthViewStore } from '../../stores/nodes/berth.view-store'
import { FlexibleBlockViewStore } from '../../stores/nodes/flexible-block.view-store'
import { LayoutObjectViewStore } from '../../stores/nodes/layout-object.view-store'
import { RailTrackViewStore } from '../../stores/nodes/rail-track.view-store'
import { RoadNetworkViewStore } from '../../stores/nodes/road-network.view-store'
import { YardBlockViewStore } from '../../stores/nodes/yard-block.view-store'
import { YardViewStore } from '../../stores/yard.view-store'
import { YardBlockLegend } from '../atoms/YardBlockLegend'
import { BerthNode } from './BerthNode'
import { FlexibleBlockNode } from './FlexibleBlockNode'
import { GateNode } from './GateNode'
import { LayoutObjectNode } from './LayoutObjectNode'
import { MapBackground } from './MapBackground'
import { RailTrackNode } from './RailTrackNode'
import { RoadNodeContextMenu } from './RoadNodeContextMenu'
import { RoadNodeNode } from './RoadNodeNode'
import { RoadSegmentContextMenu } from './RoadSegmentContextMenu'
import { RoadSegmentEdge } from './RoadSegmentEdge'
import { YardBlockNode } from './YardBlockNode'

export const nodeTypes = {
  yardBlock: YardBlockNode,
  flexibleBlock: FlexibleBlockNode,
  layoutObject: LayoutObjectNode,
  gate: GateNode,
  berth: BerthNode,
  railTrack: RailTrackNode,
  roadNode: RoadNodeNode,
}

const edgeTypes = {
  roadSegment: RoadSegmentEdge,
}

interface Props {
  yardBlockViewStore: YardBlockViewStore
  flexibleBlockViewStore: FlexibleBlockViewStore
  layoutObjectViewStore: LayoutObjectViewStore
  berthViewStore: BerthViewStore
  railTrackViewStore: RailTrackViewStore
  roadNetworkViewStore: RoadNetworkViewStore
  viewStore: YardViewStore
  isLeftDrawerOpen?: boolean
  viewportWidth: number
}

export const YardLayout = (props: Props) => (
  <ReactFlowProvider>
    <YardLayoutInner {...props} />
  </ReactFlowProvider>
)

const YardLayoutInner = observer(
  ({
    viewStore,
    yardBlockViewStore,
    flexibleBlockViewStore,
    layoutObjectViewStore,
    berthViewStore,
    railTrackViewStore,
    roadNetworkViewStore,
    isLeftDrawerOpen,
    viewportWidth,
  }: Props) => {
    const theme = useMinimalsTheme()

    const yardBlockNodes = useYardBlockNodes(viewStore, yardBlockViewStore)
    const flexibleBlockNodes = useFlexibleBlockNodes(viewStore, flexibleBlockViewStore)
    const layoutObjectNodes = useLayoutObjectNodes(viewStore, layoutObjectViewStore)
    const berthNodes = useBerthNodes(viewStore, berthViewStore)
    const railTrackNodes = useRailTrackNodes(viewStore, railTrackViewStore)
    const { roadNodeNodes, roadSegmentEdges } = useRoadNetworkNodes(viewStore, roadNetworkViewStore)

    const initialNodes = useMemo<Node[]>(() => {
      const allNodes = [
        ...roadNodeNodes,
        ...yardBlockNodes,
        ...flexibleBlockNodes,
        ...layoutObjectNodes,
        ...berthNodes,
        ...railTrackNodes,
      ]

      if (!roadNetworkViewStore.isDrawingMode) return allNodes

      return allNodes.map(node =>
        node.type === 'roadNode'
          ? node
          : {
              ...node,
              selectable: false,
              draggable: false,
              data: { ...node.data, isEditable: false, focused: false },
            },
      )
    }, [
      roadNodeNodes,
      yardBlockNodes,
      flexibleBlockNodes,
      layoutObjectNodes,
      berthNodes,
      railTrackNodes,
      roadNetworkViewStore.isDrawingMode,
    ])

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)

    const [edges, setEdges, onEdgesChange] = useEdgesState(roadSegmentEdges)
    const { onNodeDragStart, onNodeDrag, handleRoadNodeDragStop } = useRoadNodeDrag({
      roadNetworkViewStore,
      nodes,
      setNodes,
    })

    useEffect(() => {
      setNodes(prev => {
        const prevById = new Map(prev.map(n => [n.id, n]))

        return initialNodes.map(n => {
          const existing = prevById.get(n.id)
          if (!existing) return n

          // Road node positions are always authoritative from the store (updated on
          // access-point changes, road-node drag handled separately via onNodeDragStop).
          // Preserving existing.position here would prevent store-driven moves from
          // reaching the canvas.
          if (n.type === 'roadNode') return n
          return { ...n, position: existing.position }
        })
      })
    }, [initialNodes, setNodes])

    useEffect(() => {
      setEdges(roadSegmentEdges)
    }, [roadSegmentEdges, setEdges])

    const onNodeDragStop = useCallback(
      (_: React.MouseEvent, node: Node) => {
        if (viewStore.isViewMode) return

        if (handleRoadNodeDragStop(node)) return

        if (node.type === 'layoutObject' || node.type === 'gate') {
          layoutObjectViewStore.setPosition(node.id, node.position)
        } else if (node.type === 'berth') {
          berthViewStore.setPosition(node.id, node.position)
        } else if (node.type === 'railTrack') {
          railTrackViewStore.setPosition(node.id, node.position)
        } else if (node.type === 'flexibleBlock') {
          flexibleBlockViewStore.setPosition(node.id, node.position)
        } else {
          yardBlockViewStore.setPosition(node.id, node.position)
        }
      },
      [
        viewStore.isViewMode,
        handleRoadNodeDragStop,
        layoutObjectViewStore,
        berthViewStore,
        railTrackViewStore,
        flexibleBlockViewStore,
        yardBlockViewStore,
      ],
    )

    const { screenToFlowPosition } = useReactFlow()
    const pendingClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const { onMoveEnd: onElasticMoveEnd } = useElasticViewport(nodes, containerRef)

    const syncViewportCenter = useCallback(() => {
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const center = screenToFlowPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      })

      yardBlockViewStore.setViewportCenter(center)
      flexibleBlockViewStore.setViewportCenter(center)
      layoutObjectViewStore.setViewportCenter(center)
      berthViewStore.setViewportCenter(center)
      railTrackViewStore.setViewportCenter(center)
    }, [
      screenToFlowPosition,
      yardBlockViewStore,
      flexibleBlockViewStore,
      layoutObjectViewStore,
      berthViewStore,
      railTrackViewStore,
    ])

    const onMoveEnd = useCallback(
      (event: unknown, viewport: Viewport) => {
        syncViewportCenter()
        onElasticMoveEnd(event, viewport)
      },
      [syncViewportCenter, onElasticMoveEnd],
    )

    useEffect(() => {
      syncViewportCenter()
    }, [syncViewportCenter])

    const { onPaneClick, onNodeClick, onPaneContextMenu } = useYardRoadDrawing(
      roadNetworkViewStore,
      screenToFlowPosition,
      pendingClickTimer,
    )

    const geoAnchor = useGeoAnchor()

    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          '& .react-flow__node.background-object': {
            zIndex: '-1 !important',
          },
          '& .react-flow__node.background-node': {
            zIndex: '-2 !important',
          },
          '& .react-flow__node.background-node.dragging': {
            opacity: 0.6,
          },
          '& .react-flow__node.background-node.background-hidden': {
            display: 'none',
          },
          '& .react-flow__controls': {
            left: isLeftDrawerOpen ? `${YARD_DRAWERS_WIDTH}px !important` : undefined,
            transition: 'left 225ms cubic-bezier(0, 0, 0.2, 1)',
          },
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onPaneClick={onPaneClick}
          onPaneContextMenu={onPaneContextMenu}
          onNodeClick={onNodeClick}
          onMove={syncViewportCenter}
          onMoveEnd={onMoveEnd}
          nodesConnectable={false}
          nodesDraggable
          elementsSelectable={!viewStore.isViewMode}
          selectNodesOnDrag={!viewStore.isViewMode}
          fitView
          minZoom={0.05}
          zoomOnDoubleClick={false}
          proOptions={{ hideAttribution: true }}
          style={{
            backgroundColor:
              viewStore.backgroundMode === 'map' ? 'transparent' : theme.palette.grey[100],
            cursor: roadNetworkViewStore.isDrawingMode ? 'crosshair' : undefined,
          }}
        >
          {viewStore.backgroundMode !== 'map' && <Background gap={24} />}
          <Controls showInteractive={false} />
        </ReactFlow>
        {viewStore.backgroundMode === 'map' && geoAnchor && <MapBackground geoAnchor={geoAnchor} />}
        {viewStore.showLegend && (
          <YardBlockLegend viewStore={viewStore} viewportWidth={viewportWidth} />
        )}
        <RoadNodeContextMenu viewStore={roadNetworkViewStore} />
        <RoadSegmentContextMenu viewStore={roadNetworkViewStore} />
      </Box>
    )
  },
)
